/**
 * Experiments API Hooks
 * TanStack Query hooks for the experiments feature
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import {
  getRuntimeConfig,
  getRuntimeConfigFirst,
} from "../../../../../config/runtimeConfig";
import {
  getCreditCardDemoExperiment,
  getCreditCardDemoPrototype,
  isDemoCreditCardExperimentId,
} from "../utils/demoExperimentData";

import type { ExperimentType } from "../components/research/researchFoundation.constants";
import type {
  CreateExperimentData,
  ExperimentFormData,
  ExperimentProject,
  SurveyGenerationJobResponse,
  SurveyGenerationStatusResponse,
} from "../types/experiment.types";

const QUERY_KEY = ["experiment-projects"];
const EXPERIMENT_DETAIL_QUERY_KEY = ["experiment-project"];
const PROTOTYPE_STATUS_QUERY_KEY = ["prototype-status"];
const PROTOTYPE_DETAIL_QUERY_KEY = ["prototype-detail"];
const PROTOTYPE_DEPLOYMENT_STATUS_QUERY_KEY = ["prototype-deployment-status"];
const EXPERIMENT_INSIGHTS_QUERY_KEY = ["experiment-insights"];
export const PROTOTYPE_BUILD_MUTATION_KEY = ["prototype-build"] as const;
const SURVEY_GENERATION_STATUS_QUERY_KEY = ["survey-generation-status"];
const DASHBOARD_EXPERIMENTS_QUERY_KEY = ["dashboard-experiments"] as const;
const configuredApiBaseUrl = getRuntimeConfigFirst([
  "VITE_INNOVATION_API_BASE_URL",
  "VITE_API_URL",
]);

const API_BASE_URL_CANDIDATES = [
  configuredApiBaseUrl,
  "https://falcon-api-innovation-dev-ggdcgtajgnhbevb7.centralus-01.azurewebsites.net",
].filter(Boolean) as string[];

type ApiExperiment = {
  id?: string;
  name?: string;
  title?: string;
  goal?: string;
  context?: string;
  description?: string;
  owner?: string;
  audience?: string;
  type?: number;
  foundation?: {
    objective?: string;
    hypothesis?: string;
  };
  premises?: Array<{ id?: string; type?: number; value?: string }>;
  okrs?: Array<{ id?: string; okr?: string; type?: number }>;
  attachments?: Array<{
    id?: string;
    format?: string;
    filename?: string;
    url?: string;
  }>;
  status?: number | string;
  createdAt?: string;
  updatedAt?: string | null;
};

type ExperimentsApiResponse = {
  success?: boolean;
  message?: string;
  data?: {
    experiments?: ApiExperiment[];
  };
};

type ExperimentDetailApiResponse = {
  success?: boolean;
  message?: string;
  data?: ApiExperiment | { experiment?: ApiExperiment };
};

export interface ExperimentPremise {
  id?: string;
  type: number;
  value: string;
}

export interface ExperimentOkr {
  id: string;
  okr: string;
  type: number;
}

export interface ExperimentAttachment {
  id: string;
  format: string;
  filename: string;
  url: string;
}

export interface CreateExperimentRequest {
  id?: string;
  OrgId: string;
  userId: string;
  name: string;
  goal: string;
  context: string;
  audience: string;
  owner: string;
  type: number;
  foundation: {
    objective: string;
    hypothesis: string;
  };
  premises: ExperimentPremise[];
  okrs: ExperimentOkr[];
  attachments: ExperimentAttachment[];
  files?: File[];
}

export interface ExperimentDetail {
  id: string;
  name: string;
  goal: string;
  context: string;
  audience: string;
  owner: string;
  type: number;
  status?: number | string;
  createdAt?: string;
  updatedAt?: string | null;
  foundation: {
    objective: string;
    hypothesis: string;
  };
  premises: ExperimentPremise[];
  okrs: ExperimentOkr[];
  attachments: ExperimentAttachment[];
}

type CreateExperimentApiResponse = {
  success?: boolean;
  message?: string;
  data?: {
    id?: string;
    experimentId?: string;
    [key: string]: unknown;
  };
  metadata?: {
    experimentId?: string;
    [key: string]: unknown;
  };
  id?: string;
  experimentId?: string;
  [key: string]: unknown;
};

export interface CreatePrototypeRequest {
  experimentId: string;
  plan: string;
}

export interface CreatePrototypeResponse {
  iterationId?: string;
  experimentId?: string;
  status?: string | number;
  v0WebUrl?: string;
  v0DemoUrl?: string;
  trackingUrl?: string;
  message?: string;
}

export interface UpdatePrototypeRequest {
  iterationId: string;
  message: string;
}

export interface PrototypeVersion {
  versionId?: string;
  versionNumber?: number;
  demoUrl?: string;
  completedDate?: string;
}

export interface PrototypeStatusResponse {
  iterationId?: string;
  experimentId?: string;
  status?: string | number;
  v0WebUrl?: string;
  v0DemoUrl?: string;
  currentVersion?: PrototypeVersion;
  errorMessage?: string;
  createdDate?: string;
  completedDate?: string;
}

export interface PrototypeDetailVersion {
  id?: string;
  iterationId?: string;
  chatId?: string;
  versionNumber?: number;
  status?: string | number;
  v0VersionId?: string;
  v0DemoUrl?: string;
  v0ScreenshotUrl?: string;
  isCurrentVersion?: boolean;
  createdDate?: string;
  completedDate?: string;
}

export interface PrototypeDetailResponse {
  id?: string;
  iterationId?: string;
  experimentId?: string;
  summaryPlan?: string;
  status?: string | number;
  prototypeType?: string;
  v0ChatId?: string;
  v0WebUrl?: string;
  v0DemoUrl?: string;
  currentVersionId?: string;
  errorMessage?: string | null;
  createdDate?: string;
  completedDate?: string;
  deploymentId?: string | null;
  deploymentStatus?: string | number | null;
  publicUrl?: string | null;
  blobUrl?: string | null;
  deploymentErrorMessage?: string | null;
  deployedDate?: string | null;
  deploymentProgress?: number;
  versions?: PrototypeDetailVersion[];
}

export interface ConfirmPrototypeDeploymentResponse {
  message?: string;
}

export interface FeedbackFlowDeploymentStatusData {
  itretionId?: string;
  iterationId?: string;
  experimentId?: string;
  v0DemoUrl?: string;
  deploymentStatus?: string | null;
  blobUrl?: string | null;
  publicUrl?: string | null;
}

export interface FeedbackFlowDeploymentStatusResponse {
  success?: boolean;
  statusCode?: number;
  message?: string;
  data?: FeedbackFlowDeploymentStatusData | null;
  errors?: unknown;
  metadata?: Record<string, unknown> | null;
  timestamp?: string;
}

export interface PrototypeMutationResult {
  data: CreatePrototypeResponse;
  baseUrl: string;
}

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [key: string]: unknown;
}

export interface OrchestrationStartData {
  orchestrationId?: string;
  statusUrl?: string;
  message?: string;
}

export interface OrchestrationErrorData {
  message?: string | null;
  failedStage?: string | null;
  details?: string | null;
}

export interface OrchestrationStatusData {
  orchestrationId?: string;
  experimentId?: string;
  status?: string;
  progress?: number;
  currentStage?: string;
  error?: string | OrchestrationErrorData | null;
  message?: string;
  metadata?: Record<string, unknown> | null;
  v0PromptRecord?: Record<string, unknown> | null;
}

export interface OrchestrationResultData extends OrchestrationStatusData {
  plan?: string;
  summaryPlan?: string;
  implementationPlan?: string;
}

export interface ExperimentInsightsEvidenceTrace {
  participant_id?: string | null;
  participant_name?: string | null;
  user_name?: string | null;
  exemplar_quotes?: string[] | null;
  [key: string]: unknown;
}

export interface ExperimentInsightsResearchQuestionFactor {
  factor_id?: string;
  factor_name?: string;
  top_value?: string;
  top_value_posterior?: number;
}

export interface ExperimentInsightsRiskFlag {
  flag?: string;
  severity?: string;
  detail?: string;
}

export interface ExperimentInsightsResearchQuestionResult {
  rq_id?: string;
  research_question?: string;
  answer_label?: string;
  positive_posterior?: number;
  negative_posterior?: number;
  certainty?: number;
  evidence_quality?: number;
  confidence_score?: number;
  confidence_bucket?: string;
  status?: string | null;
  observed_factors?: ExperimentInsightsResearchQuestionFactor[] | null;
  risk_flags?: ExperimentInsightsRiskFlag[] | null;
  verbalized_answer?: string;
  rationale?: string;
  evidence_trace?: ExperimentInsightsEvidenceTrace[] | null;
}

export interface ExperimentInsightsFinalDecision {
  decision_label?: string;
  pursue_probability?: number;
  pivot_probability?: number;
  pause_probability?: number;
  confidence_bucket?: string;
  impact_score?: number;
  rationale?: string;
  recommended_actions?: string[] | null;
  verbalized_decision?: string;
}

export interface ExperimentInsightsGeneralInsightEvidence {
  summary?: string | null;
  quotes?: string[] | null;
}

export interface ExperimentInsightsGeneralInsight {
  insight_id?: string;
  title?: string;
  type?: string;
  support_score?: number;
  confidence_bucket?: string;
  evidence?: ExperimentInsightsGeneralInsightEvidence | null;
}

export interface ExperimentInsightsAnalysisSummary {
  participant_count?: number;
  question_coverage?: number;
  free_text_response_rate?: number;
  eligible_rq_count?: number;
  insufficient_evidence_rq_count?: number;
}

export interface ExperimentInsightsData {
  id?: string | null;
  experiment_id?: string | null;
  analysis_run_id?: string | null;
  status?: string | null;
  timestamp?: string | null;
  analysis_summary?: ExperimentInsightsAnalysisSummary | null;
  core_research_question_results?:
    | ExperimentInsightsResearchQuestionResult[]
    | null;
  final_decision?: ExperimentInsightsFinalDecision | null;
  general_insights?: ExperimentInsightsGeneralInsight[] | null;
  warnings?: string[] | null;
}

export interface ExperimentInsightsResponse {
  success?: boolean;
  statusCode?: number;
  message?: string;
  data?: ExperimentInsightsData | null;
  errors?: unknown;
  metadata?: Record<string, unknown> | null;
  timestamp?: string;
}

interface ExperimentInsightsJobEnvelope {
  id?: string | null;
  jobId?: string | null;
  experimentId?: string | null;
  status?: string | null;
  progressStage?: string | null;
  progressPercent?: number | null;
  createdAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  attempts?: number | null;
  result?: ExperimentInsightsData | null;
}

interface RawExperimentInsightsResponse extends Omit<
  ExperimentInsightsResponse,
  "data"
> {
  data?: ExperimentInsightsData | ExperimentInsightsJobEnvelope | null;
}

type OrchestrationApiResponse<T> = {
  success?: boolean;
  statusCode?: number;
  message?: string;
  data?: T;
  metadata?: Record<string, unknown> | null;
};

export interface ValidationProblemDetails extends ProblemDetails {
  errors?: Record<string, string[] | undefined>;
}

interface PayloadMetadata {
  orgId?: string;
  userId?: string;
  owner?: string;
}

const parsePlainJsonResponse = <T>(data: unknown): T => {
  if (typeof data === "string") {
    return JSON.parse(data) as T;
  }

  return data as T;
};

const parseApiErrorPayload = (
  payload: unknown,
): ProblemDetails | ValidationProblemDetails | null => {
  if (typeof payload === "string") {
    try {
      return parsePlainJsonResponse<ProblemDetails | ValidationProblemDetails>(
        payload,
      );
    } catch {
      return {
        detail: payload,
      };
    }
  }

  if (payload && typeof payload === "object") {
    return payload as ProblemDetails | ValidationProblemDetails;
  }

  return null;
};

const normalizeExperimentInsightsResponse = (
  response: RawExperimentInsightsResponse,
): ExperimentInsightsResponse => {
  const data = response.data;
  const envelope =
    data && typeof data === "object" && "result" in data
      ? (data as ExperimentInsightsJobEnvelope)
      : null;
  const nestedResult = envelope?.result ?? null;

  if (!envelope || !nestedResult) {
    return response as ExperimentInsightsResponse;
  }

  const metadataExperimentId =
    typeof response.metadata?.experimentId === "string"
      ? response.metadata.experimentId
      : null;

  return {
    ...response,
    data: {
      ...nestedResult,
      id: nestedResult.id ?? envelope.id ?? null,
      experiment_id:
        nestedResult.experiment_id ??
        envelope.experimentId ??
        metadataExperimentId ??
        null,
      analysis_run_id: nestedResult.analysis_run_id ?? envelope.jobId ?? null,
      status: nestedResult.status ?? envelope.status ?? null,
      timestamp:
        nestedResult.timestamp ??
        envelope.completedAt ??
        envelope.startedAt ??
        response.timestamp ??
        null,
    },
  };
};

const flattenValidationErrors = (
  errors?: ValidationProblemDetails["errors"],
): string[] => {
  if (!errors) return [];

  return Object.entries(errors).flatMap(([field, messages]) =>
    (messages ?? [])
      .map((message) => message?.trim())
      .filter(Boolean)
      .map((message) => `${field}: ${message}`),
  );
};

export const getExperimentApiErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message : fallback;
  }

  const parsed = parseApiErrorPayload(error.response?.data);
  const validationErrors =
    parsed && "errors" in parsed
      ? (parsed.errors as Record<string, string[] | undefined> | undefined)
      : undefined;
  const messages = flattenValidationErrors(validationErrors);
  const primaryMessage =
    parsed?.detail || parsed?.title || error.message || fallback;

  return [primaryMessage, ...messages].filter(Boolean).join(" ");
};

const fetchOrchestration = async <T>(
  path: string,
): Promise<{ baseUrl: string; response: OrchestrationApiResponse<T> }> => {
  let lastError: unknown;

  for (const baseUrl of API_BASE_URL_CANDIDATES) {
    try {
      const response = await axios.get<OrchestrationApiResponse<T>>(
        `${baseUrl}${path}`,
      );
      return { baseUrl, response: response.data };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error;
      }
      lastError = error;
    }
  }

  throw lastError ?? new Error("Failed to load orchestration data");
};

const isAxiosHttpError = (error: unknown) =>
  axios.isAxiosError(error) && Boolean(error.response);

const isNotFoundError = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 404;

export const resolvePrototypeIterationIdFromDetail = (
  prototype?: PrototypeDetailResponse | null,
): string | null => {
  const iterationCandidates = [
    prototype?.versions?.find((version) => version.isCurrentVersion)
      ?.iterationId,
    prototype?.iterationId,
    prototype?.id,
    ...(prototype?.versions ?? []).map((version) => version.iterationId),
  ];

  return (
    iterationCandidates.find((candidate): candidate is string =>
      Boolean(candidate?.trim()),
    ) ?? null
  );
};

export const getPrototypeDetailQueryKey = (
  experimentId?: string,
  baseUrl?: string,
) => [...PROTOTYPE_DETAIL_QUERY_KEY, baseUrl ?? "default", experimentId];

type PrototypeDetailBuildResponse = {
  iterationId?: string;
  experimentId?: string;
  status?: string | number;
  v0WebUrl?: string;
  v0DemoUrl?: string;
  currentVersion?: PrototypeVersion;
  errorMessage?: string | null;
  completedDate?: string;
};

export const mergePrototypeDetailWithBuildResponse = (
  previous: PrototypeDetailResponse | null | undefined,
  response: PrototypeDetailBuildResponse,
  fallbackExperimentId: string,
): PrototypeDetailResponse => {
  const status = response.status ?? "queued";
  const responseIterationId = response.iterationId?.trim();
  const experimentId =
    previous?.experimentId?.trim() ||
    response.experimentId?.trim() ||
    fallbackExperimentId;
  const responseDemoUrl =
    response.v0DemoUrl?.trim() ||
    response.currentVersion?.demoUrl?.trim() ||
    undefined;
  const responseVersionId =
    response.currentVersion?.versionId?.trim() || undefined;
  const responseCompletedDate =
    response.completedDate?.trim() ||
    response.currentVersion?.completedDate?.trim() ||
    undefined;
  const previousVersions = previous?.versions ?? [];
  const matchesResponseVersion = (version: PrototypeDetailVersion): boolean => {
    if (responseVersionId) {
      return (
        version.id?.trim() === responseVersionId ||
        version.v0VersionId?.trim() === responseVersionId
      );
    }

    return (
      Boolean(responseDemoUrl || responseCompletedDate) &&
      version.isCurrentVersion === true &&
      version.iterationId?.trim() === responseIterationId
    );
  };
  const hasResponseVersionOutput = Boolean(
    responseVersionId || responseDemoUrl || responseCompletedDate,
  );
  const shouldMergeVersion =
    Boolean(responseIterationId) && hasResponseVersionOutput;
  const versions = shouldMergeVersion
    ? [
        ...previousVersions.map((version) => {
          const isCurrentResponseVersion = matchesResponseVersion(version);

          if (!isCurrentResponseVersion) {
            return {
              ...version,
              isCurrentVersion: false,
            };
          }

          return {
            ...version,
            id: responseVersionId ?? version.id,
            iterationId: responseIterationId,
            status,
            versionNumber:
              response.currentVersion?.versionNumber ?? version.versionNumber,
            v0DemoUrl: responseDemoUrl ?? version.v0DemoUrl,
            completedDate: responseCompletedDate ?? version.completedDate,
            isCurrentVersion: true,
          };
        }),
        ...(previousVersions.some(matchesResponseVersion)
          ? []
          : [
              {
                id: responseVersionId,
                iterationId: responseIterationId,
                status,
                versionNumber: response.currentVersion?.versionNumber,
                v0DemoUrl: responseDemoUrl,
                completedDate: responseCompletedDate,
                isCurrentVersion: true,
              },
            ]),
      ]
    : previous?.versions;

  return {
    ...(previous ?? {}),
    experimentId,
    iterationId: responseIterationId ?? previous?.iterationId,
    status,
    v0WebUrl: response.v0WebUrl?.trim() || previous?.v0WebUrl,
    v0DemoUrl: responseDemoUrl ?? previous?.v0DemoUrl,
    currentVersionId: responseVersionId ?? previous?.currentVersionId,
    errorMessage: response.errorMessage?.trim() || null,
    completedDate: responseCompletedDate ?? previous?.completedDate,
    versions,
  };
};

const mapApiStatusToExperimentProjectStatus = (
  status: number | string | undefined,
): ExperimentProject["status"] => {
  const normalized =
    typeof status === "number"
      ? status
      : Number.parseInt((status ?? "").toString(), 10);

  switch (normalized) {
    case 1:
      return "validation";
    case 2:
      return "development";
    case 3:
      return "launched";
    default:
      return "ideation";
  }
};

const experimentTypeToApiType = (experimentType?: ExperimentType): number => {
  switch (experimentType) {
    case "discovery":
      return 1;
    case "validation":
      return 2;
    case "optimization":
      return 3;
    default:
      return 2;
  }
};

const getFallbackOrgId = () =>
  getRuntimeConfig("VITE_INNOVATION_ORG_ID") ||
  "76c537c7-3890-4e39-9d99-4e8aa8c858ba";

const getFallbackUserId = () => "27ad8f65-49c7-4ec7-8d37-79b1326817b3";

const toPremises = (
  type: number,
  values?: Array<string | undefined | null>,
): ExperimentPremise[] =>
  (values ?? [])
    .map((value) => (value ?? "").trim())
    .filter(Boolean)
    .map((value) => ({ type, value }));

const toExperimentFormData = (payload: CreateExperimentRequest): FormData => {
  const formData = new FormData();
  const appendIfDefined = (key: string, value?: string | number | null) => {
    if (value === undefined || value === null) return;
    formData.append(key, String(value));
  };

  appendIfDefined("Id", payload.id);
  appendIfDefined("OrganizationId", payload.OrgId);
  appendIfDefined("UserId", payload.userId);
  appendIfDefined("Name", payload.name);
  appendIfDefined("Goal", payload.goal);
  appendIfDefined("Context", payload.context);
  appendIfDefined("Audience", payload.audience);
  appendIfDefined("Owner", payload.owner);
  appendIfDefined("Type", payload.type);

  appendIfDefined("Foundation.Objective", payload.foundation?.objective ?? "");
  appendIfDefined(
    "Foundation.Hypothesis",
    payload.foundation?.hypothesis ?? "",
  );

  const premisesPayload = payload.premises ?? [];
  formData.append("Premises", JSON.stringify(premisesPayload));

  const okrsPayload = payload.okrs ?? [];
  formData.append("Okrs", JSON.stringify(okrsPayload));

  if (payload.files && payload.files.length > 0) {
    payload.files.forEach((file) => formData.append("Files", file));
  } else {
    const fileUrls =
      payload.attachments
        ?.map((item) => item.url)
        .filter((url) => !!url?.trim()) ?? [];
    formData.append("Files", JSON.stringify(fileUrls));
  }

  return formData;
};

const serializeFormArrayItems = <T>(items: T[]): string =>
  items.map((item) => JSON.stringify(item)).join(",");

const toUpdateExperimentFormData = (
  payload: CreateExperimentRequest,
): FormData => {
  const formData = toExperimentFormData(payload);
  const premisesPayload = payload.premises ?? [];
  const okrsPayload = payload.okrs ?? [];

  formData.set("Premises", serializeFormArrayItems(premisesPayload));
  formData.set("Okrs", serializeFormArrayItems(okrsPayload));

  return formData;
};

export const getExperimentProjects = async (): Promise<ExperimentProject[]> => {
  let data: ExperimentsApiResponse | undefined;
  let lastError: unknown;

  for (const baseUrl of API_BASE_URL_CANDIDATES) {
    try {
      const response = await axios.get<ExperimentsApiResponse>(
        `${baseUrl}/api/v1/experiments`,
      );
      data = response.data;
      break;
    } catch (error) {
      // Fallback for alternate backend route shapes observed in dev.
      try {
        const fallbackResponse = await axios.get<ExperimentsApiResponse>(
          `${baseUrl}/api/v1/experiments/organization/${getFallbackOrgId()}/userid${getFallbackUserId()}`,
        );
        data = fallbackResponse.data;
        break;
      } catch (fallbackError) {
        lastError = fallbackError ?? error;
      }
    }
  }

  if (!data) {
    throw lastError ?? new Error("Failed to load experiments");
  }

  const experiments = data?.data?.experiments ?? [];

  return experiments.map((item, index) => ({
    id: item.id ?? `experiment-${index}`,
    title: item.title ?? item.name ?? "Untitled Experiment",
    description:
      item.description ?? item.context ?? item.goal ?? "No description",
    status: mapApiStatusToExperimentProjectStatus(item.status),
    category: item.owner ?? "Unassigned",
    createdAt: item.createdAt ?? new Date().toISOString(),
    updatedAt: item.updatedAt ?? item.createdAt ?? new Date().toISOString(),
  }));
};

export const buildCreateExperimentPayload = (
  formData: ExperimentFormData,
  metadata?: PayloadMetadata,
): CreateExperimentRequest => {
  const okrs = (formData?.okrs ?? []) as Array<{
    id?: string;
    okr?: string;
    type?: number;
  }>;
  const attachmentsSource = (formData?.attachments ??
    formData?.supportingDocs ??
    []) as Array<{
    id?: string;
    format?: string;
    filename?: string;
    name?: string;
    url?: string;
  }>;

  return {
    OrgId: metadata?.orgId ?? formData?.orgId ?? getFallbackOrgId(),
    userId: getFallbackUserId(),
    name: (formData?.name ?? "").trim(),
    goal: (formData?.goal ?? "").trim(),
    context: (formData?.description ?? "").trim(),
    audience: (formData?.targetAudience ?? "").trim(),
    owner: metadata?.owner ?? formData?.owner ?? "Falcon User",
    type: experimentTypeToApiType(formData?.experimentType),
    foundation: {
      objective: (formData?.researchObjective ?? "").trim(),
      hypothesis: (formData?.primaryHypothesis ?? "").trim(),
    },
    premises: [
      ...toPremises(1, formData?.coreResearchQuestions),
      ...toPremises(2, formData?.keyAssumptions),
      ...toPremises(3, [
        formData?.successSignals?.qualitative,
        formData?.successSignals?.quantitative,
      ]),
    ],
    okrs: okrs
      .filter((item) => item?.okr?.trim())
      .map((item) => ({
        id: item.id ?? crypto.randomUUID(),
        okr: item.okr!.trim(),
        type: item.type ?? 0,
      })),
    attachments: attachmentsSource.map((item) => ({
      id: item.id ?? crypto.randomUUID(),
      format: item.format ?? "unknown",
      filename: item.filename ?? item.name ?? "attachment",
      url: item.url ?? "",
    })),
    files: formData?.supportingDocFiles ?? [],
  };
};

export const createExperiment = async (
  payload: CreateExperimentRequest,
): Promise<CreateExperimentApiResponse> => {
  let responseData: CreateExperimentApiResponse | undefined;
  let lastError: unknown;
  const formData = toExperimentFormData(payload);

  for (const baseUrl of API_BASE_URL_CANDIDATES) {
    try {
      const response = await axios.post<CreateExperimentApiResponse>(
        `${baseUrl}/api/v1/experiments`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      responseData = response.data;
      break;
    } catch (error) {
      // If backend responded, don't retry another host for business validation errors.
      if (axios.isAxiosError(error) && error.response) {
        throw error;
      }
      lastError = error;
    }
  }

  if (!responseData) {
    throw lastError ?? new Error("Failed to create experiment");
  }

  return responseData;
};

const normalizeExperimentDetail = (
  raw?: ApiExperiment,
): ExperimentDetail | null => {
  if (!raw?.id) {
    return null;
  }

  return {
    id: raw.id,
    name: raw.name ?? raw.title ?? "",
    goal: raw.goal ?? "",
    context: raw.context ?? raw.description ?? "",
    audience: raw.audience ?? "",
    owner: raw.owner ?? "",
    type: raw.type ?? 0,
    status: raw.status,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt ?? null,
    foundation: {
      objective: raw.foundation?.objective ?? "",
      hypothesis: raw.foundation?.hypothesis ?? "",
    },
    premises: (raw.premises ?? []).map((item) => ({
      id: item.id,
      type: item.type ?? 0,
      value: item.value ?? "",
    })),
    okrs: (raw.okrs ?? []).map((item) => ({
      id: item.id ?? crypto.randomUUID(),
      okr: item.okr ?? "",
      type: item.type ?? 0,
    })),
    attachments: (
      raw.attachments ??
      (raw as { files?: ApiExperiment["attachments"] })?.files ??
      (raw as { uploaded?: ApiExperiment["attachments"] })?.uploaded ??
      []
    ).map((item) => ({
      id: item.id ?? crypto.randomUUID(),
      format: item.format ?? "unknown",
      filename: item.filename ?? "attachment",
      url: item.url ?? "",
    })),
  };
};

export const getExperimentById = async (
  experimentId: string,
): Promise<ExperimentDetail> => {
  // Demo interception: the Credit Card Streamlining demo experiment has no
  // backing API record. Return its hardcoded metadata so the editor pages
  // (Context, Research, Prototype, Tagging, Finalize) populate as a normal
  // completed experiment. Real experiment IDs flow through unchanged.
  if (isDemoCreditCardExperimentId(experimentId)) {
    return getCreditCardDemoExperiment();
  }

  let responseData: ExperimentDetailApiResponse | undefined;
  let lastError: unknown;

  for (const baseUrl of API_BASE_URL_CANDIDATES) {
    try {
      const response = await axios.get<ExperimentDetailApiResponse>(
        `${baseUrl}/api/v1/experiments/${experimentId}`,
      );
      responseData = response.data;
      break;
    } catch (error) {
      lastError = error;
    }
  }

  if (!responseData) {
    throw lastError ?? new Error("Failed to load experiment");
  }

  const raw =
    (responseData.data as { experiment?: ApiExperiment })?.experiment ??
    (responseData.data as ApiExperiment | undefined);
  const normalized = normalizeExperimentDetail(raw);

  if (!normalized) {
    throw new Error("Experiment response missing id");
  }

  return normalized;
};

export const updateExperiment = async (
  experimentId: string,
  payload: CreateExperimentRequest,
): Promise<CreateExperimentApiResponse> => {
  let responseData: CreateExperimentApiResponse | undefined;
  let lastError: unknown;
  const formData = toUpdateExperimentFormData({
    ...payload,
    id: experimentId,
  });

  for (const baseUrl of API_BASE_URL_CANDIDATES) {
    try {
      const response = await axios.put<CreateExperimentApiResponse>(
        `${baseUrl}/api/v1/experiments/${experimentId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      responseData = response.data;
      break;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error;
      }
      lastError = error;
    }
  }

  if (!responseData) {
    throw lastError ?? new Error("Failed to update experiment");
  }

  return responseData;
};

export const createPrototype = async (
  payload: CreatePrototypeRequest,
): Promise<PrototypeMutationResult> => {
  let lastError: unknown;

  for (const baseUrl of API_BASE_URL_CANDIDATES) {
    try {
      const response = await axios.post(
        `${baseUrl}/api/Prototype?api-version=1`,
        payload,
        {
          headers: {
            Accept: "text/plain",
            "Content-Type": "application/json",
          },
        },
      );

      return {
        data: parsePlainJsonResponse<CreatePrototypeResponse>(response.data),
        baseUrl,
      };
    } catch (error) {
      if (isAxiosHttpError(error)) {
        throw new Error(
          getExperimentApiErrorMessage(
            error,
            "Failed to create prototype generation job.",
          ),
        );
      }

      lastError = error;
    }
  }

  throw lastError ?? new Error("Failed to create prototype generation job");
};

export const updatePrototype = async ({
  experimentId,
  payload,
}: {
  experimentId: string;
  payload: UpdatePrototypeRequest;
}): Promise<PrototypeMutationResult> => {
  let lastError: unknown;

  for (const baseUrl of API_BASE_URL_CANDIDATES) {
    try {
      const response = await axios.post(
        `${baseUrl}/api/Prototype/${experimentId}/update`,
        payload,
        {
          params: {
            iterationId: payload.iterationId,
            "api-version": 1,
          },
          headers: {
            Accept: "text/plain",
            "Content-Type": "application/json",
          },
        },
      );

      return {
        data: parsePlainJsonResponse<CreatePrototypeResponse>(response.data),
        baseUrl,
      };
    } catch (error) {
      if (isAxiosHttpError(error)) {
        throw new Error(
          getExperimentApiErrorMessage(
            error,
            "Failed to update prototype generation job.",
          ),
        );
      }

      lastError = error;
    }
  }

  throw lastError ?? new Error("Failed to update prototype generation job");
};

export const startExperimentOrchestration = async (
  experimentId: string,
): Promise<{ baseUrl: string; data: OrchestrationStartData | null }> => {
  const { baseUrl, response } =
    await fetchOrchestration<OrchestrationStartData>(
      `/api/v1/experiments/${experimentId}/orchestrate`,
    );

  return { baseUrl, data: response.data ?? null };
};

export const getExperimentOrchestrationStatus = async (
  experimentId: string,
  orchestrationId: string,
): Promise<OrchestrationStatusData | null> => {
  const { response } = await fetchOrchestration<OrchestrationStatusData>(
    `/api/v1/experiments/${experimentId}/orchestrate/${orchestrationId}`,
  );

  return response.data ?? null;
};

export const getExperimentOrchestrationResult = async (
  experimentId: string,
  orchestrationId: string,
): Promise<OrchestrationResultData | null> => {
  const { response } = await fetchOrchestration<OrchestrationResultData>(
    `/api/v1/experiments/${experimentId}/orchestrate/${orchestrationId}/result`,
  );

  return response.data ?? null;
};

export const getPrototypeStatus = async (
  experimentId: string,
  iterationId: string,
  baseUrl?: string,
): Promise<PrototypeStatusResponse | null> => {
  const candidateBaseUrls = baseUrl ? [baseUrl] : API_BASE_URL_CANDIDATES;
  let lastError: unknown;

  for (const candidateBaseUrl of candidateBaseUrls) {
    try {
      const response = await axios.get(
        `${candidateBaseUrl}/api/Prototype/${experimentId}/status`,
        {
          params: {
            iterationId,
            "api-version": 1,
          },
          headers: {
            Accept: "text/plain",
          },
        },
      );

      return parsePlainJsonResponse<PrototypeStatusResponse>(response.data);
    } catch (error) {
      if (isNotFoundError(error)) {
        return null;
      }

      if (isAxiosHttpError(error)) {
        throw new Error(
          getExperimentApiErrorMessage(
            error,
            "Failed to load prototype status.",
          ),
        );
      }

      lastError = error;
    }
  }

  throw lastError ?? new Error("Failed to load prototype status");
};

export const getPrototypeByExperiment = async (
  experimentId: string,
  baseUrl?: string,
): Promise<PrototypeDetailResponse | null> => {
  // Demo interception: see getExperimentById for context. Returns the
  // hardcoded Chase-styled prototype detail (status: completed, deployed,
  // demo URL pointing to /demo/chase-credit-card-application).
  if (isDemoCreditCardExperimentId(experimentId)) {
    return getCreditCardDemoPrototype();
  }

  const candidateBaseUrls = baseUrl ? [baseUrl] : API_BASE_URL_CANDIDATES;
  let lastError: unknown;

  for (const candidateBaseUrl of candidateBaseUrls) {
    try {
      const response = await axios.get(
        `${candidateBaseUrl}/api/Prototype/${experimentId}`,
        {
          params: {
            "api-version": 1,
          },
          headers: {
            Accept: "text/plain",
          },
        },
      );

      return parsePlainJsonResponse<PrototypeDetailResponse>(response.data);
    } catch (error) {
      if (isNotFoundError(error)) {
        return null;
      }

      if (isAxiosHttpError(error)) {
        throw new Error(
          getExperimentApiErrorMessage(
            error,
            "Failed to load prototype details.",
          ),
        );
      }

      lastError = error;
    }
  }

  throw lastError ?? new Error("Failed to load prototype details");
};

export const confirmPrototypeDeployment = async ({
  experimentId,
  iterationId,
}: {
  experimentId: string;
  iterationId: string;
}): Promise<{ baseUrl: string; data: ConfirmPrototypeDeploymentResponse }> => {
  let lastError: unknown;

  for (const baseUrl of API_BASE_URL_CANDIDATES) {
    try {
      const response = await axios.post(
        `${baseUrl}/api/Prototype/${experimentId}/confirm`,
        "",
        {
          params: {
            iterationId,
            "api-version": 1,
          },
          headers: {
            Accept: "*/*",
          },
        },
      );

      return {
        baseUrl,
        data: parsePlainJsonResponse<ConfirmPrototypeDeploymentResponse>(
          response.data,
        ),
      };
    } catch (error) {
      if (isAxiosHttpError(error)) {
        throw new Error(
          getExperimentApiErrorMessage(
            error,
            "Failed to confirm prototype deployment.",
          ),
        );
      }

      lastError = error;
    }
  }

  throw lastError ?? new Error("Failed to confirm prototype deployment");
};

export const getFeedbackFlowDeploymentStatus = async (
  experimentId: string,
  iterationId: string,
  baseUrl?: string,
): Promise<FeedbackFlowDeploymentStatusResponse | null> => {
  // Demo interception: return a "deployed" feedback-flow status pointing at
  // the self-hosted Chase prototype so the tagging workspace renders the
  // preview and the questions list without hitting the real API.
  if (isDemoCreditCardExperimentId(experimentId)) {
    const demoUrl =
      typeof window !== "undefined" && window.location?.origin
        ? `${window.location.origin}/demo/chase-credit-card-application`
        : "/demo/chase-credit-card-application";
    return {
      success: true,
      statusCode: 200,
      data: {
        iterationId,
        experimentId,
        v0DemoUrl: demoUrl,
        deploymentStatus: "deployed",
        publicUrl: demoUrl,
        blobUrl: demoUrl,
      },
    };
  }

  const candidateBaseUrls = baseUrl ? [baseUrl] : API_BASE_URL_CANDIDATES;
  let lastError: unknown;

  for (const candidateBaseUrl of candidateBaseUrls) {
    try {
      const response = await axios.get(
        `${candidateBaseUrl}/api/v1/experiments/${experimentId}/iterations/${iterationId}/feedback-flow/deployment-status`,
        {
          headers: {
            Accept: "text/plain",
          },
        },
      );

      return parsePlainJsonResponse<FeedbackFlowDeploymentStatusResponse>(
        response.data,
      );
    } catch (error) {
      if (isNotFoundError(error)) {
        return null;
      }

      if (isAxiosHttpError(error)) {
        throw new Error(
          getExperimentApiErrorMessage(
            error,
            "Failed to load feedback flow deployment status.",
          ),
        );
      }

      lastError = error;
    }
  }

  throw (
    lastError ?? new Error("Failed to load feedback flow deployment status")
  );
};

export const getExperimentInsights = async (
  experimentId: string,
  baseUrl?: string,
): Promise<ExperimentInsightsResponse | null> => {
  const candidateBaseUrls = baseUrl ? [baseUrl] : API_BASE_URL_CANDIDATES;
  let lastError: unknown;

  for (const candidateBaseUrl of candidateBaseUrls) {
    try {
      const response = await axios.get(
        `${candidateBaseUrl}/api/Insights/${encodeURIComponent(experimentId)}`,
        {
          headers: {
            Accept: "text/plain",
          },
        },
      );

      return normalizeExperimentInsightsResponse(
        parsePlainJsonResponse<RawExperimentInsightsResponse>(response.data),
      );
    } catch (error) {
      if (isNotFoundError(error)) {
        return null;
      }

      if (isAxiosHttpError(error)) {
        throw new Error(
          getExperimentApiErrorMessage(
            error,
            "Failed to load experiment insights.",
          ),
        );
      }

      lastError = error;
    }
  }

  throw lastError ?? new Error("Failed to load experiment insights");
};

export const updateExperimentStatus = async (
  experimentId: string,
  status: number,
): Promise<void> => {
  let lastError: unknown;
  for (const baseUrl of API_BASE_URL_CANDIDATES) {
    try {
      await axios.patch(
        `${baseUrl}/api/v1/experiments/status?experimentId=${experimentId}&status=${status}`,
        null,
        {
          headers: {
            accept: "text/plain",
          },
        },
      );
      return;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error;
      }
      lastError = error;
    }
  }
  throw lastError ?? new Error("Failed to update experiment status");
};

export const useCreateExperiment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createExperiment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
};

export const useUpdateExperiment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      experimentId,
      payload,
    }: {
      experimentId: string;
      payload: CreateExperimentRequest;
    }) => updateExperiment(experimentId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...EXPERIMENT_DETAIL_QUERY_KEY, variables.experimentId],
      });
    },
  });
};

export const useUpdateExperimentStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      experimentId,
      status,
    }: {
      experimentId: string;
      status: number;
    }) => updateExperimentStatus(experimentId, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...EXPERIMENT_DETAIL_QUERY_KEY, variables.experimentId],
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: DASHBOARD_EXPERIMENTS_QUERY_KEY,
      });
    },
  });
};

export const useCreatePrototype = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: PROTOTYPE_BUILD_MUTATION_KEY,
    mutationFn: createPrototype,
    onSuccess: (result, variables) => {
      queryClient.setQueryData<PrototypeDetailResponse | null>(
        getPrototypeDetailQueryKey(variables.experimentId),
        (previous) =>
          mergePrototypeDetailWithBuildResponse(
            previous,
            result.data,
            variables.experimentId,
          ),
      );
      queryClient.invalidateQueries({
        queryKey: [...PROTOTYPE_DETAIL_QUERY_KEY],
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: [...PROTOTYPE_STATUS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [...PROTOTYPE_DEPLOYMENT_STATUS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [...EXPERIMENT_DETAIL_QUERY_KEY, variables.experimentId],
      });
    },
  });
};

export const useConfirmPrototypeDeployment = () => {
  return useMutation({
    mutationFn: confirmPrototypeDeployment,
  });
};

export const useUpdatePrototype = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: PROTOTYPE_BUILD_MUTATION_KEY,
    mutationFn: updatePrototype,
    onSuccess: (result, variables) => {
      queryClient.setQueryData<PrototypeDetailResponse | null>(
        getPrototypeDetailQueryKey(variables.experimentId),
        (previous) =>
          mergePrototypeDetailWithBuildResponse(
            previous,
            result.data,
            variables.experimentId,
          ),
      );
      queryClient.invalidateQueries({
        queryKey: [
          ...PROTOTYPE_DETAIL_QUERY_KEY,
          "default",
          variables.experimentId,
        ],
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: [...PROTOTYPE_STATUS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [...PROTOTYPE_DEPLOYMENT_STATUS_QUERY_KEY],
      });
    },
  });
};

export const useExperimentProjects = () => {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<ExperimentProject[]> => {
      return getExperimentProjects();
    },
  });
};

export const useExperimentById = (experimentId?: string) => {
  return useQuery({
    queryKey: [...EXPERIMENT_DETAIL_QUERY_KEY, experimentId],
    queryFn: async () => getExperimentById(experimentId as string),
    enabled: !!experimentId,
  });
};

export const usePrototypeStatus = (
  experimentId?: string,
  iterationId?: string,
  options?: {
    enabled?: boolean;
    baseUrl?: string;
  },
) => {
  const isEnabled =
    Boolean(experimentId) && Boolean(iterationId) && Boolean(options?.enabled);

  return useQuery({
    queryKey: [
      ...PROTOTYPE_STATUS_QUERY_KEY,
      options?.baseUrl ?? "default",
      experimentId,
      iterationId,
    ],
    queryFn: async () =>
      getPrototypeStatus(
        experimentId as string,
        iterationId as string,
        options?.baseUrl,
      ),
    enabled: isEnabled,
    refetchInterval: isEnabled ? 10000 : false,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const usePrototypeByExperiment = (
  experimentId?: string,
  options?: {
    enabled?: boolean;
    baseUrl?: string;
    refetchInterval?: number | false;
  },
) => {
  const isEnabled = Boolean(experimentId) && (options?.enabled ?? true);

  return useQuery({
    queryKey: [
      ...PROTOTYPE_DETAIL_QUERY_KEY,
      options?.baseUrl ?? "default",
      experimentId,
    ],
    queryFn: async () =>
      getPrototypeByExperiment(experimentId as string, options?.baseUrl),
    enabled: isEnabled,
    refetchInterval: isEnabled ? (options?.refetchInterval ?? false) : false,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useFeedbackFlowDeploymentStatus = (
  experimentId?: string,
  iterationId?: string,
  options?: {
    enabled?: boolean;
    baseUrl?: string;
  },
) => {
  const isEnabled =
    Boolean(experimentId) && Boolean(iterationId) && Boolean(options?.enabled);

  return useQuery({
    queryKey: [
      ...PROTOTYPE_DEPLOYMENT_STATUS_QUERY_KEY,
      options?.baseUrl ?? "default",
      experimentId,
      iterationId,
    ],
    queryFn: async () =>
      getFeedbackFlowDeploymentStatus(
        experimentId as string,
        iterationId as string,
        options?.baseUrl,
      ),
    enabled: isEnabled,
    refetchInterval: isEnabled ? 10000 : false,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useExperimentInsights = (
  experimentId?: string,
  options?: {
    enabled?: boolean;
    baseUrl?: string;
  },
) => {
  const isEnabled = Boolean(experimentId) && Boolean(options?.enabled ?? true);
  const normalizeInsightsStatus = (status?: string | null): string => {
    const normalized = String(status ?? "")
      .trim()
      .toLowerCase();
    switch (normalized) {
      case "notgenerated":
      case "not_generated":
      case "not generated":
        return "notgenerated";
      case "generating":
      case "pending":
      case "queued":
      case "running":
      case "processing":
      case "in_progress":
        return "generating";
      case "generated":
      case "succeeded":
      case "success":
      case "completed":
      case "complete":
        return "generated";
      default:
        return normalized;
    }
  };

  return useQuery({
    queryKey: [
      ...EXPERIMENT_INSIGHTS_QUERY_KEY,
      options?.baseUrl ?? "default",
      experimentId,
    ],
    queryFn: async () =>
      getExperimentInsights(experimentId as string, options?.baseUrl),
    enabled: isEnabled,
    refetchInterval: (query) =>
      normalizeInsightsStatus(query.state.data?.data?.status) === "generating"
        ? 10000
        : false,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useCreateExperimentProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: CreateExperimentData,
    ): Promise<ExperimentProject> => {
      return {
        id: crypto.randomUUID(),
        title: data.title,
        description: data.description,
        status: "ideation",
        category: data.category,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
};

export const generateSurveyQuestions = async (
  experimentId: string,
  iterationId: string,
): Promise<SurveyGenerationJobResponse> => {
  // Demo interception: the demo experiment already has its 12 interview
  // questions hardcoded in getCreditCardDemoFeedbackFlow. Short-circuit the
  // generation job and report it as already Completed so the tagging
  // workspace doesn't poll or surface a 400 error.
  if (isDemoCreditCardExperimentId(experimentId)) {
    return {
      jobId: `demo-job-${experimentId}-${iterationId}`,
      status: "Completed",
      experimentId,
      iterationId,
      alreadyRunning: false,
    } as unknown as SurveyGenerationJobResponse;
  }

  let lastError: unknown;

  for (const baseUrl of API_BASE_URL_CANDIDATES) {
    try {
      const response = await axios.post(
        `${baseUrl}/api/v1/experiments/${experimentId}/iterations/${iterationId}/generate-survey-questions`,
      );
      return response.data.data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) throw err;
      lastError = err;
    }
  }
  throw lastError;
};

export const getSurveyGenerationStatus = async (
  experimentId: string,
  iterationId: string,
  jobId: string,
): Promise<SurveyGenerationStatusResponse> => {
  let lastError: unknown;

  for (const baseUrl of API_BASE_URL_CANDIDATES) {
    try {
      const response = await axios.get(
        `${baseUrl}/api/v1/experiments/${experimentId}/iterations/${iterationId}/generate-survey-questions/status/${jobId}`,
      );
      return response.data.data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) throw err;
      lastError = err;
    }
  }
  throw lastError;
};

export const useSurveyGenerationStatus = (
  experimentId?: string,
  iterationId?: string,
  jobId?: string,
  isEnabled?: boolean,
) => {
  const shouldPoll =
    Boolean(experimentId) &&
    Boolean(iterationId) &&
    Boolean(jobId) &&
    Boolean(isEnabled);

  return useQuery({
    queryKey: [
      ...SURVEY_GENERATION_STATUS_QUERY_KEY,
      experimentId,
      iterationId,
      jobId,
    ],
    queryFn: async () =>
      getSurveyGenerationStatus(experimentId!, iterationId!, jobId!),
    enabled: shouldPoll,
    refetchInterval: shouldPoll ? 5000 : false,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const buildExperimentPayload = buildCreateExperimentPayload;
export const useCreateExperimentMutation = useCreateExperiment;
export const useExperimentByIdQuery = useExperimentById;
export const useUpdateExperimentMutation = useUpdateExperiment;
